import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Milk, LayoutDashboard, Truck, FlaskConical, Wrench, Factory, GitBranch,
  Sparkles, LogOut, Search, Package, RotateCcw, Send, Users, Store, FileText,
  CreditCard, Settings, ChevronsLeft, ChevronsRight, Sun, Moon, Boxes, ShoppingCart,
  ShieldCheck, Activity, UserCircle, ShoppingBag, ReceiptText, Wallet, UserCog, Fingerprint,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import NotificationBell from "@/components/NotificationBell";

const groups = [
  { label: null, items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, perm: "dashboard" }] },
  {
    label: "Operación",
    items: [
      { to: "/recepcion", label: "Recepción", icon: Truck, perm: "recepcion" },
      { to: "/produccion", label: "Producción", icon: Factory, perm: "produccion" },
      { to: "/laboratorio", label: "Laboratorio", icon: FlaskConical, perm: "laboratorio" },
      { to: "/correcciones", label: "Correcciones", icon: Wrench, perm: "correcciones" },
      { to: "/trazabilidad", label: "Trazabilidad 360°", icon: GitBranch, perm: "trazabilidad" },
    ],
  },
  {
    label: "Logística",
    items: [
      { to: "/inventario", label: "Inventario", icon: Boxes, perm: "inventario" },
      { to: "/embarques", label: "Embarques", icon: Send, perm: "embarques" },
      { to: "/retornables", label: "Retornables", icon: RotateCcw, perm: "retornables" },
    ],
  },
  {
    label: "Comercial",
    items: [
      { to: "/clientes", label: "Clientes", icon: Users, perm: "clientes" },
      { to: "/ventas", label: "Ventas", icon: ShoppingCart, perm: "ventas" },
      { to: "/cobranza", label: "Cobranza", icon: CreditCard, perm: "cobranza" },
      { to: "/proveedores", label: "Proveedores", icon: Store, perm: "proveedores" },
      { to: "/compras", label: "Compras", icon: ShoppingBag, perm: "compras" },
      { to: "/cuentas-por-pagar", label: "Cuentas por pagar", icon: ReceiptText, perm: "cuentas_por_pagar" },
      { to: "/tesoreria", label: "Tesorería", icon: Wallet, perm: "tesoreria" },
      { to: "/facturacion", label: "Facturación", icon: FileText, perm: "facturacion" },
    ],
  },
  {
    label: "Inteligencia",
    items: [{ to: "/calculadora", label: "Calculadora IA", icon: Sparkles, perm: "calculadora" }],
  },
  {
    label: "Personal",
    items: [
      { to: "/checador", label: "Checador", icon: Fingerprint, perm: "checador" },
      { to: "/nomina", label: "Nómina", icon: UserCog, perm: "nomina" },
    ],
  },
  {
    label: "Sistema",
    items: [
      { to: "/parametros", label: "Parámetros", icon: Settings, perm: "parametros" },
      { to: "/usuarios", label: "Usuarios", icon: ShieldCheck, perm: "usuarios" },
      { to: "/auditoria", label: "Auditoría", icon: Activity, perm: "auditoria" },
    ],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("lp_sidebar_collapsed") === "1");
  const [dark, setDark] = useState(() => localStorage.getItem("lp_dark_mode") === "1");

  // Filtrar menú según permisos del usuario. El rol 'dueno' ve todo.
  const canView = (perm) => {
    if (!perm) return true; // Facturación (aún no controlado) siempre visible por ahora
    if (!user) return false;
    if (user.role === "dueno") return true;
    return !!user.permissions?.[perm]?.view;
  };
  const visibleGroups = groups.map((g) => ({
    ...g,
    items: g.items.filter((it) => canView(it.perm)),
  })).filter((g) => g.items.length > 0);

  useEffect(() => {
    localStorage.setItem("lp_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("lp_dark_mode", dark ? "1" : "0");
  }, [dark]);

  // Reemplaza el título genérico de la plantilla para que no aparezca en encabezados/pies de impresión
  useEffect(() => {
    document.title = "LácteoPro · ERP Lácteo";
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/buscar?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <TooltipProvider delayDuration={120}>
      <div className="min-h-screen flex bg-stone-50">
        <aside
          data-testid="sidebar"
          className={`print:hidden ${collapsed ? "w-16" : "w-64"} bg-white border-r border-stone-200 flex flex-col transition-[width] duration-200`}
        >
          {/* Brand */}
          <div className={`h-[68px] border-b border-stone-200 flex items-center ${collapsed ? "justify-center px-0" : "px-5"} gap-2 relative`}>
            <div className={`${collapsed ? "w-10 h-10" : "w-8 h-8"} rounded-md bg-[#1E3A8A] flex items-center justify-center flex-shrink-0 transition-all`}>
              <Milk className={`${collapsed ? "w-6 h-6" : "w-5 h-5"} text-white transition-all`} strokeWidth={1.75} />
            </div>
            {!collapsed && (
              <div className="overflow-hidden">
                <div className="font-outfit font-semibold text-stone-900 leading-tight truncate">LácteoPro</div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-outfit">ERP Lácteo</div>
              </div>
            )}
            <button
              onClick={() => setCollapsed((c) => !c)}
              data-testid="sidebar-toggle-btn"
              aria-label={collapsed ? "Expandir menú" : "Colapsar menú"}
              className={`absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white border border-stone-200 shadow-sm flex items-center justify-center text-stone-500 hover:text-stone-900 hover:bg-stone-50 z-10 transition-colors`}
            >
              {collapsed ? <ChevronsRight className="w-3.5 h-3.5" /> : <ChevronsLeft className="w-3.5 h-3.5" />}
            </button>
          </div>

          {/* Nav */}
          <nav className={`flex-1 ${collapsed ? "px-2" : "px-3"} py-3 space-y-3 overflow-y-auto`}>
            {visibleGroups.map((g, gi) => (
              <div key={gi}>
                {g.label && !collapsed && (
                  <div className="px-3 mt-2 mb-1 text-[10px] font-outfit font-semibold uppercase tracking-[0.12em] text-stone-400">
                    {g.label}
                  </div>
                )}
                {g.label && collapsed && gi > 0 && <div className="my-2 mx-2 border-t border-stone-100" />}
                <div className="space-y-0.5">
                  {g.items.map((n) => {
                    const link = (
                      <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.end}
                        data-testid={`nav-${n.label.toLowerCase().replace(/\s|°/g, "-")}`}
                        className={({ isActive }) =>
                          collapsed
                            ? `flex items-center justify-center w-12 h-12 mx-auto rounded-lg transition-colors ${
                                isActive
                                  ? "bg-stone-100 text-stone-900"
                                  : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
                              }`
                            : `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-ibm-plex transition-colors ${
                                isActive
                                  ? "bg-stone-100 text-stone-900 font-medium"
                                  : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
                              }`
                        }
                      >
                        <n.icon className={`${collapsed ? "w-5 h-5" : "w-4 h-4"} flex-shrink-0`} strokeWidth={1.75} />
                        {!collapsed && <span className="truncate">{n.label}</span>}
                      </NavLink>
                    );
                    if (collapsed) {
                      return (
                        <Tooltip key={n.to}>
                          <TooltipTrigger asChild>{link}</TooltipTrigger>
                          <TooltipContent side="right" className="text-xs">{n.label}</TooltipContent>
                        </Tooltip>
                      );
                    }
                    return link;
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* User + logout */}
          <div className={`${collapsed ? "p-2" : "p-3"} border-t border-stone-200`}>
            {!collapsed && (
              <NavLink to="/perfil" data-testid="nav-perfil" className={({ isActive }) => `block px-3 py-2 mb-2 rounded-md transition-colors ${isActive ? "bg-stone-100 dark:bg-stone-800" : "hover:bg-stone-50 dark:hover:bg-stone-900"}`}>
                <div className="flex items-center gap-2">
                  <UserCircle className="w-4 h-4 text-stone-500 flex-shrink-0" strokeWidth={1.75} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-stone-900 dark:text-stone-100 truncate">{user?.name}</div>
                    <div className="text-xs text-stone-500 capitalize">{user?.role}</div>
                  </div>
                </div>
              </NavLink>
            )}
            {collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={logout}
                    data-testid="logout-btn"
                    className="flex items-center justify-center w-12 h-12 mx-auto rounded-lg text-stone-600 hover:text-stone-900 hover:bg-stone-50 transition-colors"
                  >
                    <LogOut className="w-5 h-5" strokeWidth={1.75} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="text-xs">Salir ({user?.name})</TooltipContent>
              </Tooltip>
            ) : (
              <Button variant="ghost" onClick={logout} data-testid="logout-btn" className="w-full justify-start text-stone-600 hover:text-stone-900">
                <LogOut className="w-4 h-4 mr-2" strokeWidth={1.75} /> Salir
              </Button>
            )}
          </div>
        </aside>

        <main className="flex-1 flex flex-col min-w-0">
          <header className="print:hidden h-14 bg-white border-b border-stone-200 flex items-center justify-between px-6">
            <form onSubmit={onSearch} className="flex items-center gap-2 w-full max-w-md">
              <Search className="w-4 h-4 text-stone-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar folio MP-/CRE-/CUA-/CREC-/CUAC-, proveedor…"
                data-testid="global-search-input"
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-stone-400 font-ibm-plex"
              />
            </form>
            <div className="flex items-center gap-3">
              <div className="text-xs text-stone-500 font-outfit uppercase tracking-wider">{new Date().toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" })}</div>
              <NotificationBell />
              <button
                onClick={() => setDark((d) => !d)}
                data-testid="dark-mode-toggle"
                aria-label={dark ? "Modo claro" : "Modo oscuro"}
                className="w-8 h-8 rounded-md flex items-center justify-center text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors"
              >
                {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            </div>
          </header>
          <div className="flex-1 overflow-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}
